import { useCookie } from '@/react/hooks/useCookie';
import { useRevalidateGlobalCart } from '@/react/providers/global/RevalidateGlobalCartContext';
import { PREVIEW_DATE_COOKIE_NAME } from '@/services/isomorphic/PreviewService';
import { SameSite } from '@/services/models/Cookie/ICookie';
import { isNullOrEmpty } from '@/utils/null-utils';
import { startTransition, useCallback } from 'react';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { noop } from '@/utils/function-utils';
import { startPreviewServerFn } from './startPreviewServerFn';

/** The return type of the {@link usePreview} hook. */
export type UsePreviewHookResult = [
  /**
   * The timestamp (in milliseconds since epoch) of the current preview date,
   * or `null` if no preview is active. This value will never be `NaN`.
   */
  previewTimestamp: number | null,
  /**
   * Starts a preview session.
   * @param url - The URL to redirect to after starting the preview.
   * @param msSinceEpoch - The date to preview, in milliseconds since epoch,
   * or `null` to stop the preview.
   */
  setPreview: (url: URL, msSinceEpoch: number | null) => void
];

/**
 * A custom hook for managing the preview state.
 * @returns A tuple containing the current preview timestamp
 * and a function to start/end a preview session.
 */
function usePreviewNonProd(): UsePreviewHookResult {
  const revalidateGlobalCart = useRevalidateGlobalCart();

  const [previewCookie, setPreviewCookie] = useCookie(PREVIEW_DATE_COOKIE_NAME);

  let previewCookieInMsSinceEpoch = isNullOrEmpty(previewCookie)
    ? null
    : Number.parseInt(previewCookie, 10);

  previewCookieInMsSinceEpoch = Number.isNaN(previewCookieInMsSinceEpoch)
    ? null
    : previewCookieInMsSinceEpoch;

  const setPreview = useCallback(
    (url: URL, msSinceEpoch: number | null) => {
      if (Number.isNaN(msSinceEpoch)) msSinceEpoch = null;

      if (msSinceEpoch === null) {
        setPreviewCookie(null);
      } else {
        setPreviewCookie({
          value: msSinceEpoch.toString(),
          sameSite: SameSite.Lax,
          path: '/',
          secure: false // Safari doesn't set cookies with `secure` in local dev
        });
      }

      startTransition(async () => {
        revalidateGlobalCart();
        await startPreviewServerFn(url.toString(), msSinceEpoch);
      });
    },
    [setPreviewCookie, revalidateGlobalCart]
  );

  return [previewCookieInMsSinceEpoch, setPreview];
}

/**
 * A custom hook for managing the preview state.
 *
 * In production environments, this hook always returns `null` for the
 * preview timestamp and a no-op function for setting the preview.
 *
 * @returns A tuple containing the current preview timestamp
 * and a function to start/end a preview session.
 */
export const usePreview: () => UsePreviewHookResult = (process.env.NEXT_PUBLIC_APP_ENV === "prod")
  ? () => [null, noop]
  : usePreviewNonProd;
